import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "../data/site";
import { IMG, imgProps } from "../data/assets";
import { hasBooted } from "../components/Reveal";

const EASE = [0.22, 1, 0.36, 1];

// Static hero — mobile + reduced motion. White editorial canvas.
// Copy and CTAs precede the image; the headline is a styled <p> here so the
// page carries exactly ONE h1 (the desktop variant's) — never two.
function HeroStatic() {
  return (
    <section className="bg-paper pt-28" aria-label="Introduction">
      <div className="container-x pb-16">
        <p className="eyebrow">Commercial duct • exhaust • hygiene systems — India</p>
        <p className="h-display mt-8" style={{ fontSize: "clamp(40px, 9vw, 130px)" }} role="heading" aria-level="1">
          <span className="block text-charcoal">COMMERCIAL DUCT &amp;</span>
          <span className="block text-charcoal">EXHAUST CLEANING.</span>
          <span className="block text-dcc">PROVEN AT HANDOVER.</span>
        </p>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 md:text-lg">
          Survey-led, scheduled around your operations and closed with inspection footage, checklists and a documented handover.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to={SITE.cta.to} data-testid="hero-survey-cta" className="btn-primary">{SITE.cta.label} <ArrowUpRight size={16} aria-hidden="true" /></Link>
          <Link to="/our-work" data-testid="hero-verify-cta" className="btn-ghost text-charcoal">See How We Verify Our Work</Link>
        </div>
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">No obligation · Response within one working day</p>
        <p className="mt-7 flex flex-wrap gap-x-5 gap-y-2 font-mono text-xs uppercase tracking-[0.22em] text-zinc-500" data-testid="hero-trust-badges">
          {["Camera Inspection", "Access Verified", "Method Selected", "Completion Documented"].map((b) => (
            <span key={b} className="flex items-center gap-2"><span className="h-1 w-1 bg-dcc" aria-hidden="true" />{b}</span>
          ))}
        </p>
        <img src={IMG.heroMain.src} alt={IMG.heroMain.alt} {...imgProps(IMG.heroMain.src, "100vw")} fetchPriority="high"
          style={{ objectPosition: IMG.heroMain.pos }}
          className="mt-12 aspect-[16/9] w-full border border-zinc-200 object-cover" />
      </div>
    </section>
  );
}

// Restored DCC hero: white editorial canvas, large typography, deliberate image
// window on the right of the grid. One simplified, coordinated scroll timeline.
export function HeroScene() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });

  // — image window: holds still → grows slightly → becomes prominent → full-bleed
  const clip = useTransform(
    p,
    [0, 0.25, 0.5, 0.75, 0.92],
    [
      "inset(12% 4% 14% 56%)",
      "inset(12% 4% 14% 56%)",
      "inset(9% 3% 10% 44%)",
      "inset(4% 0% 4% 26%)",
      "inset(0% 0% 0% 0%)",
    ]
  );
  const imgScale = useTransform(p, [0, 1], [1.04, 1]);
  const dim = useTransform(p, [0.78, 0.95], [0, 0.4]);

  // — typography: anchored to the grid, small upward drift, exits before the image crosses it
  const textO = useTransform(p, [0.52, 0.66], [1, 0]);
  const textY = useTransform(p, [0.25, 0.66], [0, -28]);

  // — support elements
  const cueO = useTransform(p, [0.08, 0.18], [1, 0]);
  const endNoteO = useTransform(p, [0.86, 0.96], [0, 1]);

  // Both hero variants render — CSS decides visibility, so prerendered HTML
  // matches hydration on every viewport (no layout swap).
  return (
    <>
      <div className="md:hidden md:motion-reduce:block">
        <HeroStatic />
      </div>
      <div className="hidden md:block md:motion-reduce:hidden">
        <section ref={ref} className="relative h-[180vh]" aria-label="Introduction — scroll sequence">
        <div className="sticky top-0 h-screen overflow-hidden bg-paper">
          <div className="absolute inset-0 grid-lines" aria-hidden="true" />

          {/* operational image — grid-aligned window, subject right of centre */}
          <motion.div style={{ clipPath: clip }} className="absolute inset-0 will-change-[clip-path]">
            <motion.img
              src={IMG.heroMain.src}
              alt={IMG.heroMain.alt}
              {...imgProps(IMG.heroMain.src, "(max-width: 767px) 100vw, 56vw")}
              fetchPriority="high"
              style={{ scale: imgScale, objectPosition: IMG.heroMain.pos }}
              className="h-full w-full object-cover will-change-transform"
            />
            <motion.div style={{ opacity: dim }} className="absolute inset-0 bg-charcoal" />
          </motion.div>

          {/* typography block — one anchored composition; the visual headline IS the h1 */}
          <motion.div style={{ opacity: textO, y: textY }} className="absolute inset-0">
            <div className="container-x flex h-full flex-col justify-center">
              <motion.p
                initial={hasBooted() ? { opacity: 0, y: 10 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4, ease: EASE }}
                className="eyebrow absolute top-[13vh]"
              >
                Commercial duct • exhaust • hygiene systems — India
              </motion.p>
              {/* The H1 contains ONLY the headline. It renders fully visible in
                  the initial HTML — no opacity/translate entrance hiding, so it
                  reads instantly, survives JS failure, and needs no motion. */}
              <h1 className="m-0 w-[58%] p-0 font-normal">
                <span className="h-display block text-charcoal" style={{ fontSize: "clamp(40px, 4.6vw, 88px)" }}>
                  COMMERCIAL DUCT&nbsp;&amp;
                </span>
                <span className="h-display block text-charcoal" style={{ fontSize: "clamp(40px, 4.6vw, 88px)" }}>
                  EXHAUST&nbsp;CLEANING.
                </span>
                <span className="h-display block text-dcc" style={{ fontSize: "clamp(40px, 4.6vw, 88px)" }}>
                  PROVEN AT&nbsp;HANDOVER.
                </span>
              </h1>
              <motion.p
                initial={hasBooted() ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.4 }}
                className="mt-7 max-w-md text-base leading-relaxed text-zinc-600"
              >
                Survey-led, scheduled around your operations and closed with inspection footage, checklists and a documented handover.
              </motion.p>
              <motion.div
                initial={hasBooted() ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.4 }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <Link to={SITE.cta.to} data-testid="hero-survey-cta" data-cursor="GO" className="btn-primary !py-3.5 text-xs">
                  {SITE.cta.label} <ArrowUpRight size={15} aria-hidden="true" />
                </Link>
                <Link to="/our-work" data-testid="hero-verify-cta" className="btn-ghost !py-3.5 text-xs text-charcoal hover:bg-charcoal hover:text-white">
                  See How We Verify Our Work
                </Link>
              </motion.div>
              <motion.p
                initial={hasBooted() ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={{ delay: 0.45, duration: 0.4 }}
                className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500"
              >
                No obligation · Response within one working day
              </motion.p>
              <motion.p
                initial={hasBooted() ? { opacity: 0 } : false} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.4 }}
                className="mt-6 flex flex-wrap gap-x-5 gap-y-2 font-mono text-xs uppercase tracking-[0.22em] text-zinc-500"
                data-testid="hero-trust-badges"
              >
                {["Camera Inspection", "Access Verified", "Method Selected", "Completion Documented"].map((b) => (
                  <span key={b} className="flex items-center gap-2">
                    <span className="h-1 w-1 bg-dcc" aria-hidden="true" />{b}
                  </span>
                ))}
              </motion.p>
            </div>
          </motion.div>

          {/* closing note on the resolved image */}
          <motion.p style={{ opacity: endNoteO }}
            className="absolute bottom-10 left-6 font-mono text-[11px] uppercase tracking-[0.35em] text-zinc-200 md:left-12 lg:left-20">
            FASTER&nbsp;&nbsp;•&nbsp;&nbsp;SAFER&nbsp;&nbsp;•&nbsp;&nbsp;CLEANER
          </motion.p>

          {/* scroll cue — thin orange line + quiet text, bottom edge */}
          <motion.div style={{ opacity: cueO }} className="absolute bottom-7 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Scroll to explore</span>
            <motion.span
              aria-hidden="true"
              className="block h-8 w-px bg-dcc"
              animate={{ scaleY: [1, 0.4, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
        </section>
      </div>
    </>
  );
}
